import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { saveFile } from 'Shared/utils/file'
import { requestWrapper } from 'Shared/utils/network'

import { ALL_OPTION, STORE_UI } from 'Core/constants'
import { CustomGenerator, TransformResponseType } from 'Core/interfaces'
import { entityToStringLabelValue, getDefaultStoreArrayValue } from 'Core/utils'
import { entityToLabelValue } from 'Core/utils/dataFormat'

import { Document, DocumentStatus } from 'Portals/Supplier/entities'
import { isDocumentRelateToSupplierLocation } from 'Portals/Supplier/Pages/Document/TableList/Table/utils/helpers'
import { DocumentRepo } from 'Portals/Supplier/repositories'
import { RootStore } from 'Portals/Supplier/stores'
import i18n from 'Portals/Supplier/utils/i18nService'

import { DocumentTable } from './DocumentTable'

export class DocumentStore {
	table: DocumentTable
	@observable accessor documents = getDefaultStoreArrayValue<Document.Detailed>()
	@observable accessor isDocumentLoading = false
	@observable accessor suppliers = getDefaultStoreArrayValue<Entity>()
	@observable accessor supplierLocations = getDefaultStoreArrayValue<Entity>()
	@observable accessor documentTypes = getDefaultStoreArrayValue<Document.Type>()
	@observable accessor types = getDefaultStoreArrayValue<Document.Type>()
	@observable accessor statuses = getDefaultStoreArrayValue()
	@observable accessor addReplaceDocumentModalOpened = false

	constructor(private rootStore: RootStore) {
		this.table = new DocumentTable(rootStore)
	}

	@computed
	get statusesAllOptions() {
		return [ALL_OPTION, ...this.statuses.value.map(entityToStringLabelValue)]
	}

	@computed
	get typesAllOptions() {
		return [ALL_OPTION, ...this.types.value.map(entityToStringLabelValue)]
	}

	@computed
	get typesOnlySupplierOptions() {
		return this.typesAllOptions.filter(
			({ label }) => !isDocumentRelateToSupplierLocation(label)
		)
	}

	@computed
	get typesOnlyNonSupplierOptions() {
		return this.typesAllOptions.filter(({ label }) => isDocumentRelateToSupplierLocation(label))
	}

	@action.bound
	setAddReplaceDocumentModalOpened(value: boolean) {
		this.addReplaceDocumentModalOpened = value
	}

	@flow.bound
	*retrieveDocumentsBySupplier(
		supplierId: Key | undefined,
		documentTypeId: Key | undefined
	): CustomGenerator<Document.Detailed[]> {
		if (!supplierId || !documentTypeId) {
			return
		}

		yield requestWrapper({
			field: this.documents,
			repository: DocumentRepo.retrieveDocumentsBySupplier,
			repositoryParams: {
				supplierId,
				documentTypeId
			}
		})
	}

	@flow.bound
	*retrieveDocumentsByLocation(
		supplierLocationId: Key | undefined,
		documentTypeId: Key | undefined
	): CustomGenerator<Document.Detailed[]> {
		if (!supplierLocationId || !documentTypeId) {
			return
		}

		yield requestWrapper({
			field: this.documents,
			repository: DocumentRepo.retrieveDocumentsByLocation,
			repositoryParams: {
				supplierLocationId,
				documentTypeId
			}
		})
	}

	@action.bound
	clearDocuments() {
		this.documents.value = []
	}

	@flow.bound
	*downloadDocumentBySupplier(
		supplierId: Key | Nullish,
		supplierDocumentId: Key | Nullish
	): CustomGenerator<TransformResponseType> {
		if (!supplierId || !supplierDocumentId) {
			return
		}

		try {
			this.isDocumentLoading = true
			const document = yield DocumentRepo.downloadDocumentBySupplier(
				supplierId,
				supplierDocumentId
			)

			const { data, headers } = document
			saveFile({ data, headers })
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isDocumentLoading = false
		}
	}

	@flow.bound
	*downloadDocumentByLocation(
		supplierLocationId: Key | Nullish,
		supplierDocumentId: Key | Nullish
	): CustomGenerator<TransformResponseType> {
		if (!supplierLocationId || !supplierDocumentId) {
			return
		}

		try {
			this.isDocumentLoading = true

			const document = yield DocumentRepo.downloadDocumentByLocation(
				supplierLocationId,
				supplierDocumentId
			)
			const { data, headers } = document
			saveFile({ data, headers })
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isDocumentLoading = false
		}
	}

	@flow.bound
	*retrieveSuppliers(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.suppliers,
			repository: DocumentRepo.retrieveSuppliers,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveTypes() {
		yield requestWrapper({
			field: this.types,
			repository: DocumentRepo.retrieveTypes,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveSupplierLocations(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.supplierLocations,
			repository: DocumentRepo.retrieveSupplierLocations,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDocumentTypes(): CustomGenerator<Document.Type[]> {
		yield requestWrapper({
			field: this.documentTypes,
			repository: DocumentRepo.retrieveDocumentTypes,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveAddReplaceModalData() {
		try {
			yield Promise.all([
				this.retrieveSuppliers(),
				this.retrieveSupplierLocations(),
				this.retrieveDocumentTypes()
			])
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@computed
	get supplierOptions(): LabelValue<number>[] {
		return this.suppliers.value.map(entityToLabelValue)
	}

	@computed
	get supplierLocationOptions(): LabelValue<number>[] {
		return this.supplierLocations.value.map(entityToLabelValue)
	}

	@computed
	get documentTypeOptions(): LabelValue<number>[] {
		return this.documentTypes.value.map(entityToLabelValue)
	}

	@computed
	get isDocumentFormLoading(): boolean {
		return (
			this.suppliers.isLoading ||
			this.supplierLocations.isLoading ||
			this.documentTypes.isLoading
		)
	}

	@flow.bound
	*addDocumentBySupplier(
		supplierId: Key,
		payload: Document.Add.Request
	): CustomGenerator<Document.Basic> {
		if (!supplierId || !payload) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield DocumentRepo.addDocumentBySupplier(supplierId, payload)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:DOCUMENT_SAVE_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*addDocumentBySupplierLocation(
		supplierLocationId: Key,
		payload: Document.Add.Request
	): CustomGenerator<Document.Basic> {
		if (!supplierLocationId || !payload) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield DocumentRepo.addDocumentBySupplierLocation(supplierLocationId, payload)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:DOCUMENT_SAVE_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*retrieveStatuses() {
		yield requestWrapper({
			field: this.statuses,
			repository: DocumentRepo.retrieveStatuses,
			repositoryParams: null
		})
	}

	@computed
	get uploadedDocument() {
		if (!this.addReplaceDocumentModalOpened) {
			return null
		}

		return this.documents.value?.filter(
			(doc) =>
				doc?.documentStatus === DocumentStatus.Pending ||
				doc?.documentStatus === DocumentStatus.Active
		)?.[0]
	}
}
